import SizeUtility from '../../../js/Utilities/SizeUtility.js';

class TabGroup extends HTMLElement {
  activeTabIndex = 0;
  maxTabIndex = 0;
  orientation = null;
  layout = null;
  namespace = null;
  tabsDropdown = null;
  tabsSelectGroup = null;
  tabsHolder = null;
  tabList = null;
  tabs = [];
  tabPanelList = null;
  tabPanels = [];
  tabControlsList = null;
  tabControls = [];

  constructor() {
    super();

    this.layout = this.getAttribute('data-tab-config') || 'horizontal';
    this.namespace = this.id || 'tab-group';
    this.setAttribute('data-ready', true);

    this.tabsDropdown = this.querySelector('.tabs__dropdown');
    this.tabsSelectGroup = this.querySelector('select-group');
    this.tabsSelectGroupList = this.querySelector('select-group .select-group__list');
    this.tabsHolder = this.querySelector('.tabs__tabs');
    this.tabList = this.querySelector('.tabs__tablist');
    this.tabs = [...this.querySelectorAll('.tabs__tab')];
    this.tabPanelList = this.querySelector('.tabs__panels');
    this.tabPanels = [...this.querySelectorAll('.tabs__panel')];
    this.tabControlsList = this.querySelector('.tabs__controls');
    this.tabControls = [...this.querySelectorAll('.tabs__control')];

    // If we don't have any tabs, bail
    if (!this.tabs.length) {
      return;
    }

    // If more tabs than panels, remove excess tabs from the DOM
    if (this.tabs.length > this.tabPanels.length) {
      for (let i = this.tabPanels.length; i < this.tabs.length; i++) {
        this.tabs[i].parentNode.removeChild(this.tabs[i]);
      }
      this.tabs = this.tabs.slice(0, this.tabPanels.length);
    }

    // If more panels than tabs, remove excess panels from the DOM
    if (this.tabPanels.length > this.tabs.length) {
      for (let i = this.tabs.length; i < this.tabPanels.length; i++) {
        this.tabPanels[i].parentNode.removeChild(this.tabPanels[i]);
      }
      this.tabPanels = this.tabPanels.slice(0, this.tabs.length);
    }

    this.maxTabIndex = this.tabs.length - 1;

    this._onTabClick = this.onTabClick.bind(this);
    this._onControlClick = this.onControlClick.bind(this);
    this._onTabKeyDown = this.onTabKeyDown.bind(this);
    this._onPanelKeyDown = this.onPanelKeyDown.bind(this);
    this._onWindowResize = this.onWindowResize.bind(this);

    this.setupTabs();
    this.onTabClick(0, true);
  }

  connectedCallback() {
    this.addEventListeners();
  }
  disconnectedCallback() {
    this.removeEventListeners();
  }

  addEventListeners() {
    window.addEventListener('resize', this._onWindowResize);
  }
  removeEventListeners() {
    window.removeEventListener('resize', this._onWindowResize);
  }

  setupTabs() {
    if (this.tabsSelectGroupList) {
      this.tabsSelectGroupList.innerHTML = '';
    }

    if (this.tabList) {
      this.tabList.setAttribute('role', 'tablist');
    }

    if (this.tabs.length > 1 && this.tabControlsList) {
      this.tabControlsList.removeAttribute('hidden');
    }

    this.tabs.forEach((tab, i) => {
      tab.id = `${this.namespace}-tab-${i}`;
      tab.setAttribute('role', 'tab');

      tab.addEventListener('click', () => {
        if (this.tabsSelectGroup) {
          this.tabsSelectGroup.open = false;
        }
        this.onTabClick(i);
      });
      tab.addEventListener('keydown', e => this.onTabKeyDown(i, e));
    });

    if (this.tabPanelList) {
      this.tabPanelList.classList.remove('flow');
    }

    this.tabPanels.forEach((panel, i) => {
      panel.setAttribute('role', 'tabpanel');
      panel.id = `${this.namespace}-tabpanel-${i}`;
      panel.setAttribute('aria-labelledby', this.tabs[i].id);

      panel.addEventListener('keydown', e => this.onPanelKeyDown(i, e));
    });

    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-controls', this.tabPanels[i].id);
    });

    this.tabControls.forEach((control, i) => {
      control.addEventListener('click', () => {
        this.onControlClick(control.getAttribute('data-tab-dir'));
      });
    });

    this.onWindowResize();
    this.updateTabs();
  }

  updateTabs() {
    if (this.activeTabIndex < 0) {
      this.activeTabIndex = this.maxTabIndex;
    }

    if (this.activeTabIndex > this.maxTabIndex) {
      this.activeTabIndex = 0;
    }

    if (this.tabList) {
      this.tabList.setAttribute('aria-orientation', this.orientation);
    }
    this.setAttribute('data-tab-display', this.orientation);

    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', i === this.activeTabIndex);
      tab.setAttribute('tabindex', i === this.activeTabIndex ? 0 : -1);

      if (i === this.activeTabIndex) {
        if (this.tabsSelectGroup) {
          this.tabsSelectGroup.buttonText.innerHTML = tab.innerHTML;
          this.tabsSelectGroup.button.setAttribute('title', tab.textContent.trim());
        }

        if (this.isInViewport()) {
          tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    });

    this.tabPanels.forEach((panel, i) => {
      panel.setAttribute('aria-hidden', i !== this.activeTabIndex);
      if (i !== this.activeTabIndex) {
        panel.removeAttribute('tabindex');
      } else {
        panel.setAttribute('tabindex', -1);
      }
    });
  }

  onTabClick(index, isInitial = false) {
    this.activeTabIndex = index;
    this.updateTabs();

    if (!isInitial) {
      this.tabs[this.activeTabIndex].focus();
    }
  }

  onControlClick(direction = 1) {
    this.activeTabIndex += direction * 1;
    this.updateTabs();
  }

  checkNextKey(e) {
    const isNextKey = {
      horizontal: 'ArrowRight',
      mobile: 'ArrowDown',
      vertical: 'ArrowDown',
    };

    return isNextKey[this.orientation] === e.key;
  }

  checkPrevKey(e) {
    const isPrevKey = {
      horizontal: 'ArrowLeft',
      vertical: 'ArrowUp',
      mobile: 'ArrowUp',
    };

    return isPrevKey[this.orientation] === e.key;
  }

  checkFocusInKey(e) {
    const isFocusInKey = {
      horizontal: 'ArrowDown',
      vertical: 'ArrowRight',
    };

    return isFocusInKey[this.orientation] === e.key;
  }

  checkFocusOutKey(e) {
    const isFocusOutKey = {
      horizontal: 'ArrowUp',
      vertical: 'ArrowLeft',
    };

    return isFocusOutKey[this.orientation] === e.key;
  }

  onTabKeyDown(index, e) {
    if (e.key === 'Enter' || e.key === ' ') {
      this.activeTabIndex = index;
      this.updateTabs();

      return;
    }

    if (this.checkNextKey(e)) {
      this.onTabClick(index + 1);
      return;
    }

    if (this.checkPrevKey(e)) {
      this.onTabClick(index - 1);
      return;
    }

    if (this.checkFocusInKey(e)) {
      this.tabPanels[this.activeTabIndex].focus();
    }
  }

  onPanelKeyDown(index, e) {
    if (this.checkFocusOutKey(e)) {
      this.tabs[this.activeTabIndex].focus();
    }
  }

  onOrientationChange() {
    if (!this.tabsDropdown || !this.tabsHolder) return;

    if (this.orientation === 'mobile') {
      this.tabsDropdown.removeAttribute('hidden');
      this.tabsHolder.setAttribute('hidden', 'true');

      // Move tablist element to inside select-group .select-group__list
      if (this.tabsSelectGroupList) {
        this.tabsSelectGroupList.appendChild(this.tabList);
      }
    } else {
      this.tabsHolder.removeAttribute('hidden');
      this.tabsDropdown.setAttribute('hidden', 'true');

      // Move tablist element back to its original position
      if (this.tabsHolder) {
        this.tabsHolder.appendChild(this.tabList);
      }
    }
  }

  onWindowResize() {
    let currentOrientation = this.orientation;
    this.orientation = SizeUtility.isScreenGtMd() ? this.layout : 'mobile';

    if (currentOrientation !== this.orientation) {
      this.onOrientationChange();
    }

    this.updateTabs();
  }

  isInViewport() {
    let el = this;

    var rect = el.getBoundingClientRect();

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) /* or $(window).height() */ &&
      rect.right <=
        (window.innerWidth || document.documentElement.clientWidth) /* or $(window).width() */
    );
  }
}

async function init_tab_group() {
  await Promise.all([customElements.whenDefined('select-group')]).then(() => {
    customElements.define('tab-group', TabGroup);
  });
}
init_tab_group();
